import {
  CANVAS_ELEMENT_TYPES,
  isBoxElement,
} from "../nativeCanvasElements";
import { getArrowPoints, getElementFrame, isPointInBox } from "./elementBounds";
import { normalizeShapeLabelLayouts } from "./shapeTextLayout";

export const ARROW_BINDING_GAP = 6;
export const ARROW_BINDING_PROXIMITY = 12;

const COORDINATE_EPSILON = 0.01;

const getFrameCenter = (frame) => ({
  x: frame.x + frame.width / 2,
  y: frame.y + frame.height / 2,
});

/**
 * @param {object} element
 * @returns {boolean}
 */
export const isBindableElement = (element) => isBoxElement(element);

/**
 * Topmost bindable shape near the pointer, used while drawing/dragging arrows.
 * @param {{ elements: Array<object>, excludeId?: string, point: { x: number, y: number } }} params
 * @returns {object | null}
 */
export const findBindTarget = ({ elements, excludeId, point }) => {
  for (let index = elements.length - 1; index >= 0; index -= 1) {
    const element = elements[index];
    if (!isBindableElement(element) || element.id === excludeId) continue;
    const frame = getElementFrame(element);
    if (isPointInBox({ box: frame, padding: ARROW_BINDING_PROXIMITY, point })) {
      return element;
    }
  }

  return null;
};

const getRectangleRadiusToward = ({ direction, frame }) => {
  const scaleX = direction.x
    ? Math.abs(frame.width / 2 / direction.x)
    : Infinity;
  const scaleY = direction.y
    ? Math.abs(frame.height / 2 / direction.y)
    : Infinity;

  return Math.min(scaleX, scaleY);
};

const getDiamondRadiusToward = ({ direction, frame }) => {
  const normalizedSum =
    Math.abs(direction.x) / (frame.width / 2) +
    Math.abs(direction.y) / (frame.height / 2);

  return normalizedSum ? 1 / normalizedSum : 0;
};

const getEllipseRadiusToward = ({ direction, frame }) => {
  const normalizedLength = Math.hypot(
    direction.x / (frame.width / 2),
    direction.y / (frame.height / 2)
  );

  return normalizedLength ? 1 / normalizedLength : 0;
};

const getShapeRadiusToward = ({ direction, element, frame }) => {
  if (element.type === CANVAS_ELEMENT_TYPES.ELLIPSE) {
    return getEllipseRadiusToward({ direction, frame });
  }
  if (element.type === CANVAS_ELEMENT_TYPES.DIAMOND) {
    return getDiamondRadiusToward({ direction, frame });
  }

  return getRectangleRadiusToward({ direction, frame });
};

/**
 * Point on the shape outline (plus gap) along the ray from its center toward
 * `externalPoint`. Keeps arrows visually attached to the border.
 * @param {{ element: object, externalPoint: { x: number, y: number }, gap?: number }} params
 * @returns {{ x: number, y: number }}
 */
export const getBorderPointToward = ({
  element,
  externalPoint,
  gap = ARROW_BINDING_GAP,
}) => {
  const frame = getElementFrame(element);
  const center = getFrameCenter(frame);
  const direction = {
    x: externalPoint.x - center.x,
    y: externalPoint.y - center.y,
  };
  const length = Math.hypot(direction.x, direction.y);
  if (!length) return { x: frame.x + frame.width + gap, y: center.y };

  const unit = { x: direction.x / length, y: direction.y / length };
  const radius = getShapeRadiusToward({ direction: unit, element, frame });
  const distance = Math.min(radius + gap, length);

  return {
    x: center.x + unit.x * distance,
    y: center.y + unit.y * distance,
  };
};

const areCoordinatesClose = (first, second) =>
  Math.abs(first - second) < COORDINATE_EPSILON;

const isValidFixedPoint = (fixedPoint) =>
  Array.isArray(fixedPoint) &&
  fixedPoint.length === 2 &&
  fixedPoint.every(Number.isFinite);

const DEGENERATE_DIRECTION_EPSILON = 0.5;

/**
 * Where on the shape's frame an arrow endpoint attaches, as normalized
 * fractions, taken from the border point facing the pointer. Captured at
 * bind time so the attachment stays pinned to that spot afterwards
 * (Excalidraw's fixed-point binding behavior). When the pointer sits on the
 * shape's center the direction is ambiguous, so `towardFallback` (usually
 * the arrow's other endpoint) picks the side.
 * @param {{ element: object, point: { x: number, y: number }, towardFallback?: { x: number, y: number } }} params
 * @returns {[number, number]}
 */
export const getFixedPointForBinding = ({ element, point, towardFallback }) => {
  const frame = getElementFrame(element);
  const center = getFrameCenter(frame);
  const isDegenerate =
    Math.hypot(point.x - center.x, point.y - center.y) <
    DEGENERATE_DIRECTION_EPSILON;
  const borderPoint = getBorderPointToward({
    element,
    externalPoint: isDegenerate && towardFallback ? towardFallback : point,
    gap: 0,
  });

  return [
    (borderPoint.x - frame.x) / frame.width,
    (borderPoint.y - frame.y) / frame.height,
  ];
};

const getFixedAnchorPoint = ({ boundElement, fixedPoint, gap }) => {
  const frame = getElementFrame(boundElement);
  const center = getFrameCenter(frame);
  const anchor = {
    x: frame.x + fixedPoint[0] * frame.width,
    y: frame.y + fixedPoint[1] * frame.height,
  };
  const length = Math.hypot(anchor.x - center.x, anchor.y - center.y);
  if (!length) return { x: frame.x + frame.width + gap, y: center.y };

  return {
    x: anchor.x + ((anchor.x - center.x) / length) * gap,
    y: anchor.y + ((anchor.y - center.y) / length) * gap,
  };
};

const getBindingAnchorCenter = ({ binding, resolveElementById }) => {
  const boundElement = binding?.elementId
    ? resolveElementById(binding.elementId)
    : null;
  if (!boundElement) return null;

  return getFrameCenter(getElementFrame(boundElement));
};

const resolveEndpoint = ({ binding, fallbackPoint, resolveElementById, towardPoint }) => {
  const boundElement = binding?.elementId
    ? resolveElementById(binding.elementId)
    : null;
  if (!boundElement) return { binding: undefined, point: fallbackPoint };

  const gap = binding.gap ?? ARROW_BINDING_GAP;
  if (isValidFixedPoint(binding.fixedPoint)) {
    return {
      binding,
      point: getFixedAnchorPoint({
        boundElement,
        fixedPoint: binding.fixedPoint,
        gap,
      }),
    };
  }

  return {
    binding,
    point: getBorderPointToward({
      element: boundElement,
      externalPoint: towardPoint,
      gap,
    }),
  };
};

/**
 * Recomputes a bound arrow's endpoints against current shape positions.
 * Returns the same object when nothing changed, so callers can cheaply detect
 * no-ops.
 * @param {{ arrow: object, resolveElementById: (id: string) => object | undefined }} params
 * @returns {object}
 */
export const resolveArrowElement = ({ arrow, resolveElementById }) => {
  const points = getArrowPoints(arrow);
  const startFallback = { x: points.x1, y: points.y1 };
  const endFallback = { x: points.x2, y: points.y2 };
  const startCenter =
    getBindingAnchorCenter({ binding: arrow.bindings?.start, resolveElementById }) ??
    startFallback;
  const endCenter =
    getBindingAnchorCenter({ binding: arrow.bindings?.end, resolveElementById }) ??
    endFallback;

  const start = resolveEndpoint({
    binding: arrow.bindings?.start,
    fallbackPoint: startFallback,
    resolveElementById,
    towardPoint: endCenter,
  });
  const end = resolveEndpoint({
    binding: arrow.bindings?.end,
    fallbackPoint: endFallback,
    resolveElementById,
    towardPoint: startCenter,
  });

  const isUnchanged =
    areCoordinatesClose(start.point.x, points.x1) &&
    areCoordinatesClose(start.point.y, points.y1) &&
    areCoordinatesClose(end.point.x, points.x2) &&
    areCoordinatesClose(end.point.y, points.y2) &&
    start.binding === arrow.bindings?.start &&
    end.binding === arrow.bindings?.end;
  if (isUnchanged) return arrow;

  return {
    ...arrow,
    bindings:
      start.binding || end.binding
        ? { end: end.binding, start: start.binding }
        : undefined,
    x1: start.point.x,
    x2: end.point.x,
    y1: start.point.y,
    y2: end.point.y,
  };
};

/**
 * Grows shapes for wrapped labels, then resolves bound arrows against the
 * resulting geometry and drops bindings that point at deleted elements.
 * @param {Array<object>} elements
 * @returns {Array<object>}
 */
export const resolveSceneArrowBindings = (elements) => {
  const normalizedElements = normalizeShapeLabelLayouts(elements);
  const elementsById = new Map(
    normalizedElements.map((element) => [element.id, element])
  );
  const resolveElementById = (elementId) => elementsById.get(elementId);

  return normalizedElements.map((element) => {
    if (element.type !== CANVAS_ELEMENT_TYPES.ARROW) return element;
    return resolveArrowElement({ arrow: element, resolveElementById });
  });
};

/**
 * Arrows whose endpoints are bound to any of the given shape ids.
 * @param {{ elements: Array<object>, shapeIds: Set<string> }} params
 * @returns {Array<object>}
 */
export const findArrowsBoundToShapes = ({ elements, shapeIds }) =>
  elements.filter((element) => {
    if (element.type !== CANVAS_ELEMENT_TYPES.ARROW) return false;

    return (
      shapeIds.has(element.bindings?.start?.elementId) ||
      shapeIds.has(element.bindings?.end?.elementId)
    );
  });
